import { useEffect, useState } from "react";
import { roles_type, RolesApi } from "../../../../api/roles/roles.api";
import { alertError, alertSuccess } from "../../../../components/alert/swal";
import { useDispatch } from "react-redux";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import { PermissionApi } from "../../../../api/permission/permission.api";
import { setLoading, unsetLoading } from "../../../../store/loading/loading.slice";
import { routeName } from "../../../../routes/routeName";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

type PermissionAction = { code?: string; name?: string; permit?: boolean;[k: string]: any };

type PermissionThreeSub = {
    code?: string;
    name?: string;
    permit?: boolean;
    actions?: PermissionAction[];
    [k: string]: any;
};

type PermissionSub = {
    code?: string;
    name?: string;
    permit?: boolean;
    actions?: PermissionAction[];
    threeSub?: PermissionThreeSub[];  
    subMenu?: PermissionThreeSub[];  
    [k: string]: any;
};

type PermissionMain = {
    code?: string;
    name?: string;
    permit?: boolean;
    actions?: PermissionAction[];
    subMenu?: PermissionSub[];        
    [k: string]: any;
};

export const ManageStateDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate: NavigateFunction = useNavigate();

    const [permission_data, set_permission_data] = useState<PermissionMain[]>([]);
    const [form_roles, set_form_roles] = useState<{ role_name: string; id_roles?: number | null }>({
        role_name: "",
    });

    const [errorMessage, setErrorMessage] = useState<{ role_name: string }>({ role_name: "" });

    const clearErrorMessage = () => {
        setErrorMessage(prev =>
            produce(prev, draft => {
                draft.role_name = "";
            })
        );
    };

    const handleChange = (field: keyof typeof form_roles, value: string | boolean | number | null) => {
        clearErrorMessage();
        set_form_roles(prev =>
            produce(prev, draft => {
                // @ts-expect-error: union field
                draft[field] = value as any;
            })
        );
    };

    const normalizePermissions = (arr: PermissionMain[]): PermissionMain[] =>
        (arr || []).map(m => ({
            ...m,
            permit: m.permit ?? false,
            actions: (m.actions ?? []).map(a => ({ ...a, permit: a.permit ?? false })),
            subMenu: (m.subMenu ?? []).map(s => ({
                ...s,
                permit: s.permit ?? false,
                actions: (s.actions ?? []).map(a => ({ ...a, permit: a.permit ?? false })),
                threeSub: (s.threeSub ?? []).map(t => ({
                    ...t,
                    permit: t.permit ?? false,
                    actions: (t.actions ?? []).map(a => ({ ...a, permit: a.permit ?? false })),
                })),
                subMenu: (s.subMenu ?? []).map(t => ({
                    ...t,
                    permit: t.permit ?? false,
                    actions: (t.actions ?? []).map(a => ({ ...a, permit: a.permit ?? false })),
                })),
            })),
        }));

    const handleGetPermission = async () => {
        try {
            const res = await PermissionApi.getAll();
            if (res.status < 300) {
                const raw = (res.data?.data || []) as PermissionMain[];
                set_permission_data(normalizePermissions(raw));
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleCheckAllPermit = (status: boolean) => {
        set_permission_data(prev =>
            produce(prev, draft => {
                draft.forEach(main => {
                    main.permit = status;
                    main.actions?.forEach(ac => (ac.permit = status));
                    main.subMenu?.forEach(sub => {
                        sub.permit = status;
                        sub.actions?.forEach(ac => (ac.permit = status));

                        (sub.threeSub ?? []).forEach(three => {
                            three.permit = status;
                            three.actions?.forEach(ac => (ac.permit = status));
                        });

                        (sub.subMenu ?? []).forEach(three => {
                            three.permit = status;
                            three.actions?.forEach(ac => (ac.permit = status));
                        });
                    });
                });
            })
        );
    };

    // level 0: main actions
    const handleCheckMain = (main_index: number, action_index: number, status: boolean) => {
        set_permission_data(prev =>
            produce(prev, draft => {
                const ac = draft[main_index]?.actions?.[action_index];
                if (ac) ac.permit = status;
            })
        );
    };

    // level 1: sub actions
    const handleCheckSub = (main_index: number, sub_index: number, action_index: number, status: boolean) => {
        set_permission_data(prev =>
            produce(prev, draft => {
                const ac = draft[main_index]?.subMenu?.[sub_index]?.actions?.[action_index];
                if (ac) ac.permit = status;
            })
        );
    };

    // level 1: sub permit (cascade ลงชั้น 3 ทั้งสองรูปแบบ)
    const handleCheckSubPermit = (main_index: number, sub_index: number, status: boolean) => {
        set_permission_data(prev =>
            produce(prev, draft => {
                const sub = draft[main_index]?.subMenu?.[sub_index];
                if (!sub) return;
                sub.permit = status;
                sub.actions?.forEach(ac => (ac.permit = status));

                (sub.threeSub ?? []).forEach(three => {
                    three.permit = status;
                    three.actions?.forEach(ac => (ac.permit = status));
                });

                (sub.subMenu ?? []).forEach(three => {
                    three.permit = status;
                    three.actions?.forEach(ac => (ac.permit = status));
                });
            })
        );
    };

    // level 2: threeSub actions (รองรับ fallback เป็น sub.subMenu)
    const handleCheckThreeSub = (
        main_index: number,
        sub_index: number,
        threeIndex: number,
        action_index: number,
        status: boolean
    ) => {
        set_permission_data(prev =>
            produce(prev, draft => {
                const sub = draft[main_index]?.subMenu?.[sub_index];
                if (!sub) return;
                const children = (sub.threeSub && sub.threeSub.length ? sub.threeSub : sub.subMenu) ?? [];
                const ac = children?.[threeIndex]?.actions?.[action_index];
                if (ac) ac.permit = status;
            })
        );
    };

    // level 2: threeSub permit (รองรับ fallback เป็น sub.subMenu)
    const handleCheckThreeSubPermit = (
        main_index: number,
        sub_index: number,
        threeIndex: number,
        status: boolean
    ) => {
        set_permission_data(prev =>
            produce(prev, draft => {
                const sub = draft[main_index]?.subMenu?.[sub_index];
                if (!sub) return;
                const children = (sub.threeSub && sub.threeSub.length ? sub.threeSub : sub.subMenu) ?? [];
                const three = children?.[threeIndex];
                if (!three) return;
                three.permit = status;
                three.actions?.forEach(ac => (ac.permit = status));
            })
        );
    };

    // level 0: main permit (คงพฤติกรรมเดิม: cascade แค่ actions ของ main)
    const handleCheckMainPermit = (main_index: number, status: boolean) => {
        set_permission_data(prev =>
            produce(prev, draft => {
                const main = draft[main_index];
                if (!main) return;
                main.permit = status;
                main.actions?.forEach(ac => (ac.permit = status));
            })
        );
    };

    const handleSubmit = async () => {
        if (!form_roles.role_name) {
            setErrorMessage(prev =>
                produce(prev, draft => {
                    draft.role_name = t("ระบุชื่อบทบาท");
                })
            );
            return;
        }

        const body: roles_type = {
            roles_name: form_roles.role_name,
            permission: permission_data,
            status: "active",
        };

        dispatch(setLoading());
        try {
            if (id) {
                const res = await RolesApi.update(+id, body);
                if (res.status < 300) {
                    alertSuccess(t("อัพเดตข้อมูล"), t("อัพเดตข้อมูล เรียบร้อย"), undefined, () => {
                        window.location.reload();
                    });
                } else {
                    alertError(t("อัพเดตข้อมูล"), t("อัพเดตข้อมูล ไม่สำเร็จ"));
                }
            } else {
                const res = await RolesApi.create(body);
                if (res.status < 300) {
                    alertSuccess(t("เพิ่มข้อมูล"), t("เพิ่มข้อมูล เรียบร้อย"));
                    navigate(routeName.cms_website.permission);
                } else {
                    alertError(t("เพิ่มข้อมูล"), t("เพิ่มข้อมูล ไม่สำเร็จ"));
                }
            }
        } catch (err) {
            console.log(err);
            alertError(id ? t("อัพเดตข้อมูล") : t("เพิ่มข้อมูล"), id ? t("อัพเดตข้อมูล ไม่สำเร็จ") : t("เพิ่มข้อมูล ไม่สำเร็จ"));
        } finally {
            dispatch(unsetLoading());
        }
    };

    const handleGetOne = async (id_roles: number) => {
        dispatch(setLoading());
        try {
            const res = await RolesApi.findOne(id_roles);
            if (res.status < 300) {
                const data = res.data?.data;
                set_form_roles(prev =>
                    produce(prev, draft => {
                        draft.role_name = data?.roles_name ?? "";
                    })
                );

                const fetched = (res.data?.data?.permission || []) as PermissionMain[];
                set_permission_data(normalizePermissions(fetched));
            }
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(unsetLoading());
        }
    };

    useEffect(() => {
        if (id && !isNaN(+id)) {
            handleGetOne(+id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!id) {
            handleGetPermission();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        permission_data,
        handleCheckAllPermit,
        handleCheckMain,
        handleSubmit,
        handleCheckMainPermit,
        handleCheckSub,
        handleCheckSubPermit,
        form_roles,
        errorMessage,
        handleChange,
        handleCheckThreeSub,
        handleCheckThreeSubPermit,
    };
};
